import { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import api from "../lib/api";
import { usePlayer } from "../context/PlayerContext";
import { useAuth } from "../context/AuthContext";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import VideoCard from "../components/VideoCard";
import { SteelFace, CutLetters, AddToPlaylistPanel, shareSong } from "../components/MiniPlayer";
import { askForLogin } from "../lib/askLogin";

const SITE = "Dan The Man TV";

// Changes one of the page's <meta> tags (making it when it is not there) and returns what puts it back as it was.
function setMeta(attr, name, value) {
  let tag = document.querySelector(`meta[${attr}="${name}"]`);
  const made = !tag;
  const before = tag ? tag.getAttribute("content") : null;
  if (made) { tag = document.createElement("meta"); tag.setAttribute(attr, name); document.head.appendChild(tag); }
  tag.setAttribute("content", value);
  return () => {
    if (made) tag.remove();
    else if (before === null) tag.removeAttribute("content");
    else tag.setAttribute("content", before);
  };
}

// Takes one <meta> tag off the page and returns what puts it back in the same place.
function hideMeta(attr, name) {
  const tag = document.querySelector(`meta[${attr}="${name}"]`);
  if (!tag || !tag.parentNode) return () => {};
  const parent = tag.parentNode;
  const next = tag.nextSibling;
  parent.removeChild(tag);
  return () => { if (!tag.parentNode) parent.insertBefore(tag, next && next.parentNode === parent ? next : null); };
}

// What the browser tab and a search result show for a song: its name, who wrote it, and the site that hosts it
// (the artist is the writer, the site is the host). The description is the song's own text, kept short.
// An explicit song gets the plain line, so adult text never lands in a search result.
function songHead(v) {
  const title = String((v && v.title) || "").trim();
  const artist = String((v && v.artist) || "").trim();
  if (!title) return null;
  const by = artist && artist.toLowerCase() !== SITE.toLowerCase() ? ` – ${artist}` : "";
  let description = v.explicit ? "" : String(v.description || "").replace(/\s+/g, " ").trim();
  if (description.length > 155) description = description.slice(0, 155).replace(/\s+\S*$/, "") + "…";
  if (!description) description = `${title}${artist ? ` by ${artist}` : ""} on ${SITE}: AI music, music videos and entertainment. No ads.`;
  return { title: `${title}${by} | ${SITE}`, description };
}

// What Google is told about a song page, as video information (JSON-LD): the song's name, description, picture, video file,
// length and date. It is only given for a song whose video file a visitor can really watch on the page: a Premium song gives
// its free preview clip (the server swaps it in for guests and free members), and an explicit song, or a locked one with no
// clip, gives nothing. The sitemap (backend/seo.py) says the same things.
function songVideoInfo(v, origin) {
  if (!v || v.explicit || v.age_locked) return null;
  const title = String(v.title || "").trim();
  if (!title) return null;
  const artist = String(v.artist || "").trim();
  const full = (u) => { try { return u ? new URL(u, origin).href : ""; } catch { return ""; } };
  const contentUrl = full(v.video_url);
  const cover = full(v.cover_url);
  if (!contentUrl || !cover) return null;
  let description = String(v.description || "").replace(/\s+/g, " ").trim();
  if (!description) description = `${title}${artist ? ` by ${artist}` : ""} on ${SITE}: AI music, music videos and entertainment. No ads.`;
  if (description.length > 2048) description = description.slice(0, 2048).replace(/\s+\S*$/, "");
  const info = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: artist && artist.toLowerCase() !== SITE.toLowerCase() ? `${title} – ${artist}` : title,
    description,
    thumbnailUrl: [cover],
    contentUrl,
    url: `${origin}/watch/${encodeURIComponent(v.id)}`,
    isFamilyFriendly: true,
    publisher: { "@type": "Organization", name: SITE, url: `${origin}/` },
  };
  const day = String(v.release_date || v.created_at || "").slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(day)) info.uploadDate = `${day}T12:00:00+00:00`;   // Google wants a time and a time zone, not only the day
  const secs = Math.round(Number(v.locked ? v.preview_seconds : v.duration_seconds));
  if (secs >= 1 && secs <= 28800) {
    const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
    info.duration = "PT" + (h ? `${h}H` : "") + (m ? `${m}M` : "") + (s || (!h && !m) ? `${s}S` : "");
  }
  const words = (Array.isArray(v.tags) ? v.tags : []).map((t) => String(t).trim()).filter(Boolean);
  if (words.length) info.keywords = words.join(", ");
  return info;
}

export default function Watch() {
  const { id } = useParams();
  const location = useLocation();
  const [v, setV] = useState(null);
  const [related, setRelated] = useState([]);
  const [showPl, setShowPl] = useState(false);                // the "Add to playlist" box
  const [lyrics, setLyrics] = useState({ id: null, text: "" });  // the words of the song (kept apart from the song by the server)
  const player = usePlayer();
  const { user } = useAuth();

  // When a card is clicked, VideoCard passes the originating page's list via
  // navigation state so Play uses that remembered list starting at this song.
  // If the page was opened directly (no state), fall back to [this + related].
  const sourceQueue = Array.isArray(location.state?.sourceQueue) ? location.state.sourceQueue : null;
  const sourceIsPlaylist = !!location.state?.playlist;   // the song was opened from Favorites: Autoplay and Shuffle work in that list

  useEffect(() => {
    api.get(`/videos/${id}`).then((r) => {
      setV(r.data);
      const g = r.data.genres?.[0];
      if (g) api.get(`/videos?genre=${g}&limit=12`).then((r2) => setRelated(r2.data.filter(x => x.id !== id)));
    });
  }, [id, user]);

  // The lyrics are asked for on their own: the server leaves them out of an explicit song until the member has passed the 18+ check.
  useEffect(() => {
    let dead = false;
    api.get(`/videos/${id}/lyrics`)
      .then((r) => { if (!dead) setLyrics({ id, text: (r.data && r.data.lyrics) || "" }); })
      .catch(() => {});
    return () => { dead = true; };
  }, [id, user]);

  // Give the tab, and the description that Google reads, this song's own name (see songHead above).
  // Leaving the page, or opening another song, puts the site's own title and description back.
  useEffect(() => {
    if (!v || v.id !== id) return undefined;
    const head = songHead(v);
    if (!head) return undefined;
    const before = document.title;
    let tag = document.querySelector('meta[name="description"]');
    const made = !tag;
    const oldText = tag ? tag.getAttribute("content") : "";
    if (made) { tag = document.createElement("meta"); tag.setAttribute("name", "description"); document.head.appendChild(tag); }
    document.title = head.title;
    tag.setAttribute("content", head.description);
    return () => {
      document.title = before;
      if (made) tag.remove(); else tag.setAttribute("content", oldText);
    };
  }, [v, id]);

  // Tell Google this page is a music video (see songVideoInfo above). Taken away again when the page is left.
  useEffect(() => {
    if (!v || v.id !== id) return undefined;
    const info = songVideoInfo(v, window.location.origin);
    if (!info) return undefined;
    const tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.setAttribute("data-dtm-video", id);
    tag.textContent = JSON.stringify(info).replace(/</g, "\\u003c");
    document.head.appendChild(tag);
    return () => { tag.remove(); };
  }, [v, id]);

  // The page's social tags (og: and twitter:) say the same title, words and cover as the video information above.
  // index.html puts the site's own share picture and the home title in them on every address, so on a song page they named
  // a different picture and title than the video's own. Google asks for the same title and thumbnail everywhere a page names
  // them, and reports "No thumbnail URL provided" when they disagree. The site's tags come back when the page is left.
  useEffect(() => {
    if (!v || v.id !== id) return undefined;
    const info = songVideoInfo(v, window.location.origin);
    const head = songHead(v);
    if (!info || !head) return undefined;
    const cover = info.thumbnailUrl[0];
    const undo = [
      setMeta("property", "og:title", info.name),
      setMeta("property", "og:description", head.description),
      setMeta("property", "og:image", cover),
      setMeta("name", "twitter:image", cover),
      // the size and picture words in index.html belong to the site's own picture, not to this cover
      hideMeta("property", "og:image:width"),
      hideMeta("property", "og:image:height"),
      hideMeta("property", "og:image:alt"),
    ];
    return () => { undo.reverse().forEach((f) => f()); };
  }, [v, id]);

  if (!v) return null;

  const playFromSource = () => {
    if (sourceQueue && sourceQueue.length > 0) {
      const idx = sourceQueue.findIndex((x) => x.id === id);
      player.playQueue(sourceQueue, idx >= 0 ? idx : 0, { playlist: sourceIsPlaylist });
      return;
    }
    player.playQueue([v, ...related], 0);
  };

  const like = async () => {
    if (!user) { toast.error("Sign in to save favorites"); return; }
    try {
      if (v.is_liked) { await api.delete(`/favorites/${id}`); setV({ ...v, is_liked: false }); }
      else { await api.post(`/favorites/${id}`); setV({ ...v, is_liked: true }); toast.success("Saved"); }
    } catch (err) { void err; }
  };

  // The link and the phone's share sheet are in one place (components/MiniPlayer.js), shared with the big player's Share button.
  const share = () => shareSong(v);

  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-black aspect-video">
            {v.video_url && v.cover_url && !v.age_locked ? (
              // A real video tag showing the cover as its picture (nothing is downloaded until Play): it is how Google sees
              // that this page holds a video. The Play button below starts the song in the player as before.
              // A song with no cover keeps the plain picture: a video tag with no picture is what Google reports as having no thumbnail.
              <video src={v.video_url} poster={v.cover_url} preload="none" muted playsInline aria-hidden="true" tabIndex={-1}
                data-testid="watch-poster-video" className="w-full h-full object-cover" />
            ) : (
              <img src={v.cover_url} alt="" className="w-full h-full object-cover" />
            )}
            <button
              data-testid="watch-poster-play"
              onClick={playFromSource}
              className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/20"
            >
              <span className="steel-btn steel-btn--primary w-20 h-20"><SteelFace glyph="play" bevel /></span>
            </button>
          </div>
          <div className="mt-4 flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <div className="min-w-0">
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold" data-testid="watch-title">{v.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-3 text-zinc-500">
                {/* the artist's name, cut out of a steel plate and lit from behind */}
                <Link to={`/videos?artist=${encodeURIComponent(v.artist)}`} data-testid="watch-artist-link" className="steel-plate cn-plate">
                  <CutLetters text={v.artist} />
                </Link>
                {v.album && <span>• {v.album}</span>}
              </div>
            </div>
            <div className="flex items-center gap-4 shrink-0 pl-1 sm:pl-0 sm:pt-1">
              <button data-testid="watch-play-btn" onClick={playFromSource} title="Play" aria-label="Play" className="steel-btn steel-btn--primary w-12 h-12"><SteelFace glyph="play" bevel /></button>
              <button data-testid="watch-share-btn" onClick={share} title="Copy share link" aria-label="Share" className="steel-btn w-11 h-11">
                <SteelFace glyph="share" />
              </button>
              <button data-testid="watch-like-btn" onClick={like} title={v.is_liked ? "Remove from favorites" : "Add to favorites"} aria-label={v.is_liked ? "Remove from favorites" : "Add to favorites"} aria-pressed={!!v.is_liked}
                className={`steel-btn w-11 h-11 ${v.is_liked ? "is-liked is-lit" : ""}`}>
                <SteelFace glyph="heart" />
              </button>
              <button data-testid="watch-add-playlist-btn" onClick={() => { if (user) setShowPl(true); else askForLogin("playlist"); }} title="Add to playlist" aria-label="Add to playlist" className="steel-btn w-11 h-11"><SteelFace glyph="plus" /></button>
              {showPl && user && (
                <AddToPlaylistPanel fixed song={v} many={!!(user.is_premium || user.role === "admin")} onClose={() => setShowPl(false)} />
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-4">
            {v.tags.map((t) => (
              <Link key={t} to={`/videos?q=${encodeURIComponent(t)}`} className="text-xs px-2 py-1 rounded-full bg-white/5 border border-white/10 hover:border-cyan-400/40 text-zinc-300">{t}</Link>
            ))}
          </div>
          <p className="mt-4 text-zinc-300">{v.description}</p>
          {lyrics.id === id && lyrics.text && (
            <section className="mt-8" data-testid="watch-lyrics">
              <h2 className="font-display text-2xl font-bold mb-3">Lyrics</h2>
              <p className="whitespace-pre-line text-zinc-300 leading-relaxed">{lyrics.text}</p>
            </section>
          )}
        </div>
        <aside>
          <div className="bg-zinc-900/60 border border-white/10 rounded-xl p-5 mb-4">
            <div className="font-mono-ui text-xs uppercase tracking-widest text-cyan-400 mb-3">Details</div>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-zinc-500">Genres</dt><dd>{v.genres.join(", ")}</dd></div>
              <div className="flex justify-between"><dt className="text-zinc-500">Language</dt><dd>{v.language}</dd></div>
              <div className="flex justify-between"><dt className="text-zinc-500">Country</dt><dd>{v.country}</dd></div>
              {(v.moods || []).length > 0 && (
                <div className="flex justify-between gap-4"><dt className="text-zinc-500">Moods</dt>
                  <dd className="flex flex-wrap justify-end gap-1.5" data-testid="watch-moods">
                    {v.moods.map((m) => (
                      <Link key={m} to={`/videos?mood=${encodeURIComponent(m)}`} className="text-xs px-2 py-0.5 rounded-full bg-fuchsia-400/10 border border-fuchsia-400/30 text-fuchsia-300 hover:border-fuchsia-400/60">{m}</Link>
                    ))}
                  </dd>
                </div>
              )}
              <div className="flex justify-between"><dt className="text-zinc-500">Plays</dt><dd>{v.play_count?.toLocaleString()}</dd></div>
              {v.premium && <div className="flex items-center gap-2 text-amber-400 pt-2"><Sparkles className="w-4 h-4" /> Premium exclusive</div>}
            </dl>
          </div>
        </aside>
      </div>
      {related.length > 0 && (
        <div className="mt-12">
          <h2 className="font-display text-2xl font-bold mb-4">More like this</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {related.map((r) => <VideoCard key={r.id} video={r} size="sm" queue={related} />)}
          </div>
        </div>
      )}
    </div>
  );
}
